/**
 * Panneau de douche
 */
import * as THREE from "three";
import Experience from "../../../Experience.js";

export default class ShowerPanel {
  constructor(
    scale,
    position = new THREE.Vector3(0, 0, 0),
    srcTexture,
    subType,
    rotation
  ) {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.room = this.experience.room;

    this.scale = scale;
    this.subType = subType;
    this.rotation = rotation || new THREE.Euler(0, 0, 0);

    this.length = this.scale.x;
    this.height = this.scale.y;

    if (this.height == 2.5) this.height = 2.42;
    // if revêtement mur
    if (this.subType == "wall") {
      this.thickness = 0.005;
    } else if (this.subType === "slim") {
      this.thickness = 0.018;
    } else if (this.subType === "kitDecor") {
      this.thickness = 0.012;
    } else {
      this.thickness = 0.06;
    }
    this.position = position;
    this.srcTexture = srcTexture || null;

    this.textureLoader = this.experience.textureLoader;

    this.objectManager = this.experience.objectManager;

    // Textures for showerPanel bas
    this.roughnessMap =
      this.subType === "bas"
        ? this.textureLoader.load("./products/showerPanel/roughness.jpg")
        : null;

    // Normal map for revetements murs shower
    /*     this.normalTexture =
      this.subType === "wall"
        ? this.textureLoader.load("./products/wall/normalMap.jpg")
        : null; */

    if (this.subType === "wall") {
      this.normalTexture = this.textureLoader.load(
        "./products/wall/normalMap.jpg"
      );
    } else if (this.subType === "kitDecor") {
      this.normalTexture = this.textureLoader.load(
        "./products/showerPanel/kitDecor/normalMap120x260.jpg"
      );
    } else {
      this.normalTexture = null;
    }

    if (this.normalTexture !== null) {
      this.normalTexture.repeat.set(this.length / 3.5, 0.85);
      this.normalTexture.wrapS = THREE.RepeatWrapping;
      this.normalTexture.wrapT = THREE.RepeatWrapping;
    }

    this.material = [
      new THREE.MeshStandardMaterial({ color: 0xffffff }), // +X
      new THREE.MeshStandardMaterial({ color: 0xffffff }), // -X
      new THREE.MeshStandardMaterial({ color: 0xffffff }), // +Y
      new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughnessMap: this.roughnessMap,
        normalMap: this.normalTexture,
      }), // -Y
      new THREE.MeshStandardMaterial({ color: 0xffffff }), // +Z (intérieur)
      new THREE.MeshStandardMaterial({ color: 0xffffff }), // -Z
    ];
    this.geometry = new THREE.BoxGeometry(
      this.length,
      this.height,
      this.thickness
    );

    this.geometry.translate(0, this.height / 2, 0);

    // Creation shower panel
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.position.copy(this.position);
    if(this.subType === "bas"){
      this.mesh.position.y = 0.045;
    } else {
      this.mesh.position.y = 0.04;
    }
    this.mesh.userData.collision = false;
    this.mesh.userData.snapping = false;
    this.mesh.name = "showerPanel";
    this.mesh.userData.type = "showerPanel";
    this.mesh.userData.subType = this.subType;
    this.mesh.userData.instance = this;
    this.mesh.userData.model = this.mesh;
    this.mesh.userData.isBoxGeometry = true;
    this.mesh.userData.scale = this.scale;
    this.mesh.userData.axesMove = [true, false, true];

    if (this.rotation) {
      this.mesh.rotation.copy(this.rotation);
    }

    if (this.srcTexture !== null) {
      this.setTexture(this.srcTexture);
    }

    this.objectManager.addObjectCollision(this.mesh);
    this.objectManager.addObject(this.mesh);

    this.scene.add(this.mesh);
  }

  /**
   * Clone instance
   */
  cloneInstance() {
    const clonedInstance = new ShowerPanel(
      this.scale.clone(), // scale
      new THREE.Vector3(0, 0, 0), //  position
      this.mesh.userData.texture, // texture
      this.subType, // subType
      null //  rotation
    );
    return clonedInstance;
  }

  /**
   * Set length of shower panel
   * @param {number} newLength - New length of shower panel.
   */
  setLength(newLength) {
    this.length = newLength;
    this.geometry.dispose();
    this.geometry = new THREE.BoxGeometry(
      this.length,
      this.height,
      this.thickness
    );
    this.mesh.geometry = this.geometry;
  }

  setTexture(textureUrl) {
    this.textureLoader.load(
      textureUrl,
      (texture) => {
        // Configure la répétition de la texture si nécessaire
        texture.colorSpace = THREE.SRGBColorSpace;
        if (this.subType !== "bas") {
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          if (this.subType == "wall") {
            texture.repeat.set(this.length / 3.5, 0.85);
          } else if (this.subType == "kitDecor") {
            texture.repeat.set(this.length / 1.5, 0.85);
          }
           else {
            texture.repeat.set(this.length, this.height);
          }
        }

        // Exception panneau bas noir
        if (
          textureUrl ===
          "./products/showerPanel/bas/PB2000950V2-noir/product.jpg"
        ) {
          this.material[0] = new THREE.MeshStandardMaterial({
            color: 0x4e4e4e,
          });
          this.material[1] = new THREE.MeshStandardMaterial({
            color: 0x4e4e4e,
          });
          this.material[2] = new THREE.MeshStandardMaterial({
            color: 0x4e4e4e,
          });
        } else {
          this.material[0] = new THREE.MeshStandardMaterial({
            color: 0xffffff,
          });
          this.material[1] = new THREE.MeshStandardMaterial({
            color: 0xffffff,
          });
          this.material[2] = new THREE.MeshStandardMaterial({
            color: 0xffffff,
          });
        }

        // Créez un tableau de 6 matériaux
        this.material[4] = new THREE.MeshStandardMaterial({
          map: texture,
          roughnessMap: this.roughnessMap,
          normalMap: this.normalTexture,
        });
        this.material[5] = new THREE.MeshStandardMaterial({ map: texture });

        // Assurez-vous que le matériau est mis à jour
        this.mesh.material = this.material;
        this.mesh.userData.texture = textureUrl;
      },
      undefined,
      (error) => {
        console.error("Erreur lors du chargement de la texture :", error);
      }
    );
  }

  /**
   * Function for duplicate object
   */
  clone() {
    const clonedInstance = new ShowerPanel();

    if (this.geometry) {
      clonedInstance.geometry = this.geometry.clone();
      clonedInstance.mesh.geometry = clonedInstance.geometry;
    }

    if (this.material) {
      clonedInstance.material = this.material.map((mat) => mat.clone());
      clonedInstance.mesh.material = clonedInstance.material;
    }

    clonedInstance.mesh.position.copy(this.mesh.position);
    const offset = 0.5;
    clonedInstance.mesh.position.x += offset;
    clonedInstance.mesh.position.z += offset;
    clonedInstance.mesh.rotation.copy(this.mesh.rotation);
    clonedInstance.mesh.scale.copy(this.mesh.scale);

    clonedInstance.mesh.userData = { ...this.mesh.userData };
    delete clonedInstance.mesh.userData.instance;
    this.mesh.userData.model = clonedInstance.mesh;
    clonedInstance.mesh.userData.instance = clonedInstance;

    clonedInstance.mesh.name = "showerPanel";
    clonedInstance.mesh.userData.id = Date.now();

    return clonedInstance;
  }
}
